import { T } from "../Tokens";

// ── Section card wrapper ──────────────────────────────────────────
function Card({ children, style }) {
  return (
    <div
      style={{
        background: T.bg2,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusLg,
        padding: "16px 18px",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Card title row ────────────────────────────────────────────────
function CardTitle({ icon, iconBg, children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        marginBottom: 14,
        paddingBottom: 10,
        borderBottom: `1px solid ${T.border}`,
      }}
    >
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: T.radiusSm,
          background: iconBg,
          border: `1px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.textSecondary,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {children}
      </span>
    </div>
  );
}

// ── Metric card ───────────────────────────────────────────────────
function MetricCard({ value, label, color }) {
  return (
    <div
      style={{
        background: T.bg2,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: "14px 10px",
        textAlign: "center",
        flex: 1,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          color,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1,
          marginBottom: 5,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 9,
          color: T.textDim,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
    </div>
  );
}

// ── Pipeline ──────────────────────────────────────────────────────
const PIPELINE_STEPS = [
  {
    num: "1",
    title: "User submits claim",
    sub: "Text input via React UI",
    accent: false,
  },
  {
    num: "2",
    title: "Intent classification",
    sub: "Chat vs. verification routing",
    accent: false,
  },
  {
    num: "3",
    title: "RAG retrieval",
    sub: "Top-K articles via cosine similarity",
    accent: true,
  },
  {
    num: "4",
    title: "Fake/Real Classification",
    sub: "74.19% F1 — fake/real prediction",
    accent: true,
  },
  {
    num: "5",
    title: "LLM reasoning",
    sub: "Evidence-grounded verdict + citations",
    accent: true,
  },
];

function Pipeline() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {PIPELINE_STEPS.map((step, i) => (
        <div key={step.num}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 11 }}>
            {/* number badge */}
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 600,
                background: T.greenDim,
                border: `1px solid ${T.greenBorder}`,
                color: T.greenText,
                marginTop: 1,
              }}
            >
              {step.num}
            </div>
            <div
              style={{ paddingBottom: i < PIPELINE_STEPS.length - 1 ? 14 : 0 }}
            >
              <div
                style={{ fontSize: 12, color: T.textPrimary, marginBottom: 2 }}
              >
                {step.title}
              </div>
              <div style={{ fontSize: 10, color: T.textDim }}>{step.sub}</div>
            </div>
          </div>
          {/* connector line */}
          {i < PIPELINE_STEPS.length - 1 && (
            <div
              style={{
                width: 1,
                height: 10,
                background: T.border,
                marginLeft: 11,
                marginBottom: 0,
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ── Tech stack ────────────────────────────────────────────────────
const TECH_STACK = [
  { name: "FNN", role: "Classifier", color: T.green },
  { name: "MongoDB", role: "Vector Store", color: T.blue },
  { name: "Ollama", role: "Embeddings", color: T.amber },
  { name: "Spring Boot", role: "Java API", color: T.green },
  { name: "News API", role: "Data Source", color: T.red },
  { name: "Gemini", role: "LLM", color: T.textSecondary },
  { name: "React", role: "Frontend", color: T.blue },
  { name: "FastAPI", role: "ML Layer", color: T.green },
];

function TechStack() {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
        gap: 8,
      }}
    >
      {TECH_STACK.map(({ name, role, color }) => (
        <div
          key={name}
          style={{
            background: T.bg3,
            border: `1px solid ${T.border}`,
            borderRadius: T.radius,
            padding: "9px 11px",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: color,
              flexShrink: 0,
              boxShadow: `0 0 5px ${color}80`,
            }}
          />
          <div>
            <div
              style={{ fontSize: 11, color: T.textPrimary, fontWeight: 500 }}
            >
              {name}
            </div>
            <div style={{ fontSize: 9, color: T.textDim }}>{role}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Team ──────────────────────────────────────────────────────────
const TEAM = [
  {
    initials: "MS",
    name: "Madhur Srivastav",
    sap: "500119876",
    role: "RAG, Backend and Cloud Developer",
    color: T.green,
  },
  {
    initials: "SK",
    name: "Sonakshi Kundu",
    sap: "500123866",
    role: "Frontend Developer",
    color: T.blue,
  },
  {
    initials: "MB",
    name: "Mahi Bhardwaj",
    sap: "500120132",
    role: "ML Engineer",
    color: T.red,
  },
  {
    initials: "RN",
    name: "Rohan Negi",
    sap: "500119593",
    role: "Database Administrator",
    color: T.amber,
  },
];

function Avatar({ initials, color, size = 34 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        flexShrink: 0,
        background: `${color}22`,
        border: `1px solid ${color}55`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 600,
        color,
      }}
    >
      {initials}
    </div>
  );
}

function Team() {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
        gap: 8,
      }}
    >
      {TEAM.map(({ initials, name, sap, role, color }) => (
        <div
          key={name}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: T.bg3,
            border: `1px solid ${T.border}`,
            borderRadius: T.radius,
            padding: "10px 12px",
          }}
        >
          <Avatar initials={initials} color={color} />

          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: 12, color: T.textPrimary }}>{name}</div>
            <div style={{ fontSize: 10, color: T.textSecondary }}>
              SAP: {sap}
            </div>
            <div style={{ fontSize: 10, color: color }}>{role}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────
const IconCheck = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <circle cx="6" cy="6" r="5" stroke={T.green} strokeWidth="1.2" />
    <path
      d="M4 6l1.5 1.5 3-3"
      stroke={T.green}
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);
const IconStack = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <rect
      x="1"
      y="1"
      width="10"
      height="10"
      rx="2"
      stroke={T.blue}
      strokeWidth="1.2"
    />
    <path
      d="M4 6h4M6 4v4"
      stroke={T.blue}
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);
const IconUser = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <circle cx="6" cy="4" r="2.5" stroke={T.amber} strokeWidth="1.2" />
    <path
      d="M1 11c0-2.76 2.24-5 5-5s5 2.24 5 5"
      stroke={T.amber}
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);
const IconTeam = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <circle cx="4" cy="4" r="2" stroke={T.green} strokeWidth="1.2" />
    <circle cx="8" cy="4" r="2" stroke={T.green} strokeWidth="1.2" />
    <circle cx="4" cy="8" r="2" stroke={T.green} strokeWidth="1.2" />
    <circle cx="8" cy="8" r="2" stroke={T.green} strokeWidth="1.2" />
  </svg>
);

// ── About Page ────────────────────────────────────────────────────
export default function AboutUs() {
  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: T.bg0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Page header */}
      <div
        style={{
          background: T.bg1,
          borderBottom: `1px solid ${T.border}`,
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: T.radius,
            background: T.greenDim,
            border: `1px solid ${T.greenBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill={T.green}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: T.textPrimary }}>
            About
          </div>
          <div style={{ fontSize: 10, color: T.textDim }}>
            FactoScope · 2026
          </div>
        </div>
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          padding: "20px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        {/* Hero */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "18px 20px",
            background: T.bg2,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusLg,
            animation: "fadeUp 0.3s ease both",
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              flexShrink: 0,
              background: T.greenDim,
              border: `1px solid ${T.greenBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill={T.green}>
              <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z" />
            </svg>
          </div>
          <div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: T.textPrimary,
                lineHeight: 1.2,
              }}
            >
              Facto<span style={{ color: T.green }}>Scope</span>
            </div>
            <div
              style={{
                fontSize: 11,
                color: T.textDim,
                marginTop: 4,
                fontFamily: T.fontMono,
              }}
            >
              AI-powered fake news detection & verification system
            </div>
          </div>
        </div>

        {/* Metrics */}
        {/* <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            animation: "fadeUp 0.3s ease 0.05s both",
          }}
        >
          <MetricCard value="96.6%" label="BERT F1-Score" color={T.green} />
          <MetricCard value="MongoDB" label="Vector Store" color={T.blue} />
          <MetricCard value="1.9s" label="Avg Response" color={T.amber} />
          <MetricCard value="60%" label="Fake Detection Rate" color={T.red} />
        </div> */}

        {/* Pipeline + Tech/Mentor */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            animation: "fadeUp 0.3s ease 0.1s both",
          }}
          className="about-grid"
        >
          {/* Pipeline */}
          <Card>
            <CardTitle icon={IconCheck} iconBg={`${T.green}18`}>
              End-to-end Pipeline
            </CardTitle>
            <Pipeline />
          </Card>

          {/* Tech + Mentor stacked */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Card>
              <CardTitle icon={IconStack} iconBg={`${T.blue}18`}>
                Tech Stack
              </CardTitle>
              <TechStack />
            </Card>

            <Card>
              <CardTitle icon={IconUser} iconBg={`${T.amber}18`}>
                Mentor
              </CardTitle>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  background: T.bg3,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.radius,
                  padding: "10px 14px",
                }}
              >
                <Avatar initials="RK" color={T.amber} size={36} />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12,
                      color: T.textPrimary,
                      fontWeight: 500,
                    }}
                  >
                    Dr. Rohitesh Kumar
                  </div>
                  <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>
                    Data Science Cluster, SoCS, UPES
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    padding: "2px 7px",
                    borderRadius: T.radiusSm,
                    letterSpacing: "0.06em",
                    background: T.amberDim,
                    border: `1px solid ${T.amberBorder}`,
                    color: T.amberText,
                    textTransform: "uppercase",
                  }}
                >
                  Mentor
                </span>
              </div>
            </Card>
          </div>
        </div>

        {/* Team */}
        <Card style={{ animation: "fadeUp 0.3s ease 0.15s both" }}>
          <CardTitle icon={IconTeam} iconBg={`${T.green}18`}>
            Team
          </CardTitle>
          <Team />
        </Card>

        {/* Footer */}
        <div style={{ textAlign: "center", padding: "4px 0 8px" }}>
          <span style={{ fontSize: 10, color: T.textDim }}>
            FactoScope · 2026
          </span>
        </div>
      </div>

      {/* Responsive: collapse grid to single column on mobile */}
      <style>{`
        @media (max-width: 640px) {
          .about-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
